import React, { useState, useEffect } from 'react';
import { Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

// ALWAYS use relative URL - same-origin requests only
const API = '/api';

const Pricing = ({ user: propsUser }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(propsUser);
  const [loading, setLoading] = useState(null);

  useEffect(() => {
    if (!propsUser) {
      axios.get(`${API}/auth/me`, { withCredentials: true })
        .then(res => setUser(res.data))
        .catch(() => {});
    }
  }, [propsUser]);

  const handleSubscribe = async (planType) => {
    if (!user) {
      navigate('/login');
      return;
    }

    setLoading(planType);

    try {
      const originUrl = window.location.origin;
      const response = await axios.post(
        `${API}/checkout/session`,
        { plan_type: planType, origin_url: originUrl },
        { withCredentials: true }
      );

      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment failed');
      setLoading(null);
    }
  };

  const plans = [
    {
      name: 'Beta Starter',
      price: '$49',
      period: '/month',
      pricePerScan: '50 scans included',
      description: 'Perfect for small businesses',
      features: [
        '50 compliance scans per month',
        'Multi-jurisdiction compliance analysis',
        'Platform policy checks',
        'Risk score with violation breakdown',
        'AI-generated compliant rewrites',
        'Downloadable PDF compliance report',
        'Email support'
      ],
      planType: 'starter',
      popular: false,
      cta: 'Join Beta'
    },
    {
      name: 'Pro',
      price: '$129',
      period: '/month',
      pricePerScan: '200 scans included',
      description: 'For growing agencies',
      features: [
        'Everything in Beta Starter',
        '200 compliance scans per month',
        'Bulk scanning',
        'Suggested legal disclaimers',
        'Priority support'
      ],
      planType: 'pro',
      popular: true,
      cta: 'Upgrade to Pro'
    },
    {
      name: 'Agency',
      price: '$299',
      period: '/month',
      pricePerScan: '600 scans included',
      description: 'For enterprise teams',
      features: [
        'Everything in Pro',
        '600 compliance scans per month',
        'Team access',
        'White-label PDF reports',
        'Audit log history',
        'Advanced bulk processing'
      ],
      planType: 'agency',
      popular: false,
      cta: 'Start Agency Plan'
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar user={user} />

      <main className="flex-1 py-16 px-6">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-4" data-testid="pricing-title">Simple, Transparent Pricing</h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Start with 3 free scans. No credit card required. Upgrade anytime to increase your limits.
            </p>
            <div className="mt-4 inline-block px-4 py-2 bg-primary/10 border border-primary/20 rounded-lg">
              <p className="text-sm text-primary font-medium">
                Beta pricing – Early access rates. Pricing may increase as regulatory coverage and integrations expand.
              </p>
            </div>
          </div>

          {/* Subscription Plans */}
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto mb-12">
            {plans.map((plan) => (
              <Card
                key={plan.planType}
                className={`relative ${plan.popular ? 'border-2 border-primary shadow-xl scale-105' : 'border'}`}
                data-testid={`plan-${plan.planType}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-accent text-accent-foreground px-4 py-1">
                      Most Popular
                    </Badge>
                  </div>
                )}
                <CardHeader className="pb-4">
                  <CardTitle className="text-xl">{plan.name}</CardTitle>
                  <CardDescription>{plan.description}</CardDescription>
                  <div className="mt-3">
                    <span className="text-4xl font-bold font-heading">{plan.price}</span>
                    <span className="text-muted-foreground">{plan.period}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{plan.pricePerScan}</p>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 mb-6">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start" data-testid={`feature-${plan.planType}-${index}`}>
                        <Check className="h-4 w-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    className="w-full"
                    variant={plan.popular ? "default" : "outline"}
                    size="lg"
                    onClick={() => handleSubscribe(plan.planType)}
                    disabled={loading === plan.planType}
                    data-testid={`subscribe-${plan.planType}-btn`}
                  >
                    {loading === plan.planType ? 'Processing...' : (plan.cta || 'Get Started')}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <Separator className="my-8" />

          {/* All Plans Include */}
          <div className="text-center max-w-3xl mx-auto">
            <h3 className="font-heading text-xl font-semibold mb-6">All Plans Include</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center justify-center">
                <Check className="h-4 w-4 text-green-600 mr-2" />
                <span className="text-sm">7-day free trial</span>
              </div>
              <div className="flex items-center justify-center">
                <Check className="h-4 w-4 text-green-600 mr-2" />
                <span className="text-sm">Cancel anytime</span>
              </div>
              <div className="flex items-center justify-center">
                <Check className="h-4 w-4 text-green-600 mr-2" />
                <span className="text-sm">Data encryption</span>
              </div>
              <div className="flex items-center justify-center">
                <Check className="h-4 w-4 text-green-600 mr-2" />
                <span className="text-sm">Regular updates</span>
              </div>
            </div>
          </div>

          {/* FAQ or Trust Badges */}
          <div className="mt-12 text-center">
            <p className="text-sm text-muted-foreground">
              Questions? Contact us at <a href="mailto:support@scanmyad.com" className="text-primary hover:underline">support@scanmyad.com</a>
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              Secure payments powered by Stripe. Your data is protected.
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Pricing;
